import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { addDays, addWeeks, addMonths, format } from 'date-fns';
import { nb } from 'date-fns/locale';
import { ChevronLeft, ChevronRight, Plus, Lock, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { DayView, WeekView, MonthView } from './TennisCalendarViews';
import { BookingList } from './TennisBookingList';
import { BookingDialog, BlockDialog } from './TennisBookingDialogs';
import { toKey, rangeFor, apiFetch, pad, bookingPhase, isBlock } from './tennisCalendarUtils';

const VIEWS = [['day', 'Dag'], ['week', 'Uke'], ['month', 'Måned']];
const PHASES = [['all', 'Alle'], ['upcoming', 'Kommende'], ['active', 'Pågår'], ['past', 'Tidligere'], ['cancelled', 'Kansellerte']];
const selectCls = 'h-9 rounded-md border border-slate-300 bg-white px-2 text-sm text-slate-900';

const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
const title = (view, anchor) => {
  if (view === 'day') return cap(format(anchor, 'EEEE d. MMMM yyyy', { locale: nb }));
  if (view === 'week') { const [s, e] = rangeFor('week', anchor); return `Uke ${format(anchor, 'I')} · ${format(s, 'd. MMMM', { locale: nb })} – ${format(e, 'd. MMMM yyyy', { locale: nb })}`; }
  return cap(format(anchor, 'MMMM yyyy', { locale: nb }));
};

export const TennisCalendarTab = ({ members, onChanged }) => {
  const [view, setView] = useState('week');
  const [anchor, setAnchor] = useState(new Date());
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [court, setCourt] = useState(0);
  const [memberQ, setMemberQ] = useState('');
  const [phase, setPhase] = useState('all');
  const [dialog, setDialog] = useState(null);

  const [start, end] = rangeFor(view, anchor);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiFetch(`/admin/calendar?start=${toKey(start)}&end=${toKey(end)}&include_cancelled=true`);
      setBookings(data.bookings);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [toKey(start), toKey(end)]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const refresh = () => { load(); onChanged?.(); };

  const step = (dir) => setAnchor(view === 'day' ? addDays(anchor, dir) : view === 'week' ? addWeeks(anchor, dir) : addMonths(anchor, dir));

  const filtered = useMemo(() => {
    const q = memberQ.trim().toLowerCase();
    return bookings.filter((b) =>
      (!court || b.court_number === court) &&
      (!q || `${b.member_name} ${b.member_email || ''} ${b.member_phone || ''}`.toLowerCase().includes(q))
    );
  }, [bookings, court, memberQ]);

  const listed = useMemo(() => filtered.filter((b) => {
    if (phase === 'all') return true;
    if (phase === 'cancelled') return b.status === 'cancelled';
    return b.status !== 'cancelled' && bookingPhase(b) === phase;
  }), [filtered, phase]);

  const calendarBookings = useMemo(() => filtered.filter((b) => b.status !== 'cancelled'), [filtered]);
  const stats = useMemo(() => ({
    bookings: calendarBookings.filter((b) => !isBlock(b)).length,
    blocks: calendarBookings.filter(isBlock).length,
    cancelled: filtered.filter((b) => b.status === 'cancelled').length,
  }), [calendarBookings, filtered]);

  const openFree = (date, courtNo, hour) => setDialog({ type: 'booking', initial: { date, court_number: courtNo, time_slot: pad(hour), duration_hours: 1 } });
  const openBooking = (b) => setDialog({ type: 'booking', initial: b });
  const pickDay = (d) => { setAnchor(d); setView('day'); };

  return (
    <div className="space-y-4" data-testid="tennis-calendar-tab">
      <div className="bg-white rounded-2xl border border-slate-200 p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1">
            <Button variant="outline" size="icon" data-testid="cal-prev" onClick={() => step(-1)} aria-label="Forrige"><ChevronLeft className="w-4 h-4" /></Button>
            <Button variant="outline" size="sm" data-testid="cal-today" onClick={() => setAnchor(new Date())}>I dag</Button>
            <Button variant="outline" size="icon" data-testid="cal-next" onClick={() => step(1)} aria-label="Neste"><ChevronRight className="w-4 h-4" /></Button>
          </div>
          <h3 className="font-bold text-slate-900 flex-1 min-w-[200px] normal-case tracking-normal" data-testid="cal-title">{title(view, anchor)}</h3>
          <div className="flex rounded-lg bg-slate-100 p-1" role="tablist">
            {VIEWS.map(([v, label]) => (
              <button key={v} type="button" role="tab" aria-selected={view === v} data-testid={`cal-view-${v}`} onClick={() => setView(v)} className={`px-3 py-1.5 text-sm font-semibold rounded-md transition-colors ${view === v ? 'bg-[#CCFF00] text-black' : 'text-slate-600 hover:text-slate-900'}`}>{label}</button>
            ))}
          </div>
          <Button data-testid="cal-new-booking" className="bg-[#CCFF00] text-black hover:bg-[#b8e600] font-bold" size="sm" onClick={() => setDialog({ type: 'booking', initial: { date: toKey(anchor) } })}><Plus className="w-4 h-4 mr-1" />Ny booking</Button>
          <Button data-testid="cal-new-block" variant="outline" size="sm" onClick={() => setDialog({ type: 'block' })}><Lock className="w-4 h-4 mr-1" />Blokker tid</Button>
          <Button variant="ghost" size="icon" data-testid="cal-refresh" onClick={load} aria-label="Oppdater"><RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /></Button>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <select data-testid="cal-filter-court" className={selectCls} value={court} onChange={(e) => setCourt(Number(e.target.value))}>
            <option value={0}>Alle baner</option>
            <option value={1}>Bane 1</option>
            <option value={2}>Bane 2</option>
          </select>
          <Input data-testid="cal-filter-member" className="h-9 w-56" placeholder="Filtrer på medlem…" value={memberQ} onChange={(e) => setMemberQ(e.target.value)} />
          <select data-testid="cal-filter-phase" className={selectCls} value={phase} onChange={(e) => setPhase(e.target.value)}>
            {PHASES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <span className="ml-auto text-xs text-slate-500" data-testid="cal-stats">{stats.bookings} bookinger · {stats.blocks} blokkeringer · {stats.cancelled} kansellerte</span>
        </div>
        <div className="flex flex-wrap gap-3 text-xs text-slate-500">
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-green-500/15 border border-green-500/40" />Booket</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-slate-200 border border-slate-300" />Blokkert</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-slate-50 border border-dashed border-slate-300" />Ledig – klikk for å booke</span>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-3 sm:p-4">
        {view === 'day' && <DayView date={anchor} bookings={calendarBookings} courtFilter={court} onFree={openFree} onBooking={openBooking} />}
        {view === 'week' && <WeekView date={anchor} bookings={calendarBookings} courtFilter={court} onFree={openFree} onBooking={openBooking} onPickDay={pickDay} />}
        {view === 'month' && <MonthView date={anchor} bookings={calendarBookings} onPickDay={pickDay} />}
      </div>

      <div>
        <h4 className="font-bold text-slate-900 mb-2">Bookinger i perioden ({listed.length})</h4>
        <BookingList bookings={listed} onEdit={openBooking} />
      </div>

      <BookingDialog open={dialog?.type === 'booking'} initial={dialog?.initial} members={members} onClose={() => setDialog(null)} onSaved={refresh} />
      <BlockDialog open={dialog?.type === 'block'} initialDate={toKey(anchor)} onClose={() => setDialog(null)} onSaved={refresh} />
    </div>
  );
};
